import { useState, useEffect, useRef } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import {
  Menu, X, Sun, Moon, ChevronDown, ArrowRight, House, Phone,
  Receipt, Fingerprint, Printer, CreditCard, Scan,
  Battery, Wifi, Camera, Monitor, Laptop,
  Utensils, Calendar, GraduationCap, DollarSign, Bus, Home,
  BookOpen, ClipboardList, Package, Users, MessageSquare,
} from 'lucide-react'
import Button from './ui/Button'
import { useTheme } from '../hooks/useTheme'

const NAV_LINKS = [
  { to: '/features', label: 'Features' },
  { to: '/products', label: 'Products' },
  { to: '/about',    label: 'About'    },
  { to: '/contact',  label: 'Contact'  },
]

const NAV_FEATURES = [
  { id: 'feeding',        name: 'Feeding',        Icon: Utensils       },
  { id: 'attendance',     name: 'Attendance',     Icon: Calendar       },
  { id: 'academics',      name: 'Academics',      Icon: GraduationCap  },
  { id: 'finance',        name: 'Finance',        Icon: DollarSign     },
  { id: 'transport',      name: 'Transport',      Icon: Bus            },
  { id: 'hostel',         name: 'Hostel',         Icon: Home           },
  { id: 'library',        name: 'Library',        Icon: BookOpen       },
  { id: 'admissions',     name: 'Admissions',     Icon: ClipboardList  },
  { id: 'inventory',      name: 'Inventory',      Icon: Package        },
  { id: 'parents',        name: 'Parents',        Icon: Users          },
  { id: 'communications', name: 'Communications', Icon: MessageSquare  },
]

const NAV_PRODUCTS = [
  { id: 'payment-receipts',   name: 'Payment Receipts',      Icon: Receipt     },
  { id: 'attendance-tracking',name: 'Biometric Attendance',  Icon: Fingerprint },
  { id: 'document-printing',  name: 'Document Printer',      Icon: Printer     },
  { id: 'id-cards',           name: 'ID Card Printing',      Icon: CreditCard  },
  { id: 'barcode-scanning',   name: 'ID Card Scanner',       Icon: Scan        },
  { id: 'backup-power',       name: 'Backup Power',          Icon: Battery     },
  { id: 'network-setup',      name: 'Campus Networking',     Icon: Wifi        },
  { id: 'security-cameras',   name: 'Security Cameras',      Icon: Camera      },
  { id: 'desktop-computers',  name: 'Desktop Computers',     Icon: Monitor     },
  { id: 'laptops',            name: 'Staff & Teacher Laptops', Icon: Laptop    },
]

// Real Tvantra brand mark (cropped from the brand kit). Shown as-is in both
// themes — no theme-adaptive fallback, matching what the brand asset shows.
function LogoMark({ size = 40 }) {
  return (
    <img
      src="/tvantra-mark.png"
      alt=""
      width={size}
      height={size}
      style={{ width: size, height: size, objectFit: 'contain' }}
    />
  )
}

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false)
  const [open, setOpen] = useState(false)
  const [productsOpen, setProductsOpen] = useState(false)
  const [featuresOpen, setFeaturesOpen] = useState(false)
  const closeTimer = useRef(null)
  const featuresCloseTimer = useRef(null)
  const { pathname } = useLocation()
  const { theme, toggleTheme, mounted } = useTheme()
  // Until mounted, match the server-rendered (light) icon to avoid a #418
  // hydration mismatch; flip to the real theme icon after hydration.
  const isDark = mounted && theme === 'dark'

  const openProducts = () => {
    clearTimeout(closeTimer.current)
    setProductsOpen(true)
  }

  const closeProducts = () => {
    closeTimer.current = setTimeout(() => setProductsOpen(false), 120)
  }

  const openFeatures = () => {
    clearTimeout(featuresCloseTimer.current)
    setFeaturesOpen(true)
  }

  const closeFeatures = () => {
    featuresCloseTimer.current = setTimeout(() => setFeaturesOpen(false), 120)
  }

  useEffect(() => {
    const handler = () => setScrolled(window.scrollY > 20)
    handler()
    window.addEventListener('scroll', handler, { passive: true })
    return () => window.removeEventListener('scroll', handler)
  }, [pathname])

  useEffect(() => { setOpen(false); setProductsOpen(false); setFeaturesOpen(false) }, [pathname])

  const activeCls = 'text-ink border-b border-theme-strong'
  const inactiveCls = 'text-[var(--ink-soft)] hover:text-ink border-b border-transparent'

  return (
    <header
      className={`relative z-50 transition-all duration-200 navbar-surface ${
        scrolled ? 'border-b border-theme shadow-card' : 'border-b border-transparent'
      }`}
    >
      <nav className="max-w-7xl mx-auto px-4 sm:px-6 flex items-center justify-between h-16">

        {/* Logo */}
        <Link to="/" className="flex items-center gap-2.5">
          <LogoMark size={36} />
          <div className="flex flex-col leading-none">
            <span className="font-display font-bold text-base tracking-tight text-ink">
              MySchoolPortal
            </span>
            <span className="text-xs font-medium text-[var(--ink-soft)]">by Tvantra</span>
          </div>
        </Link>

        {/* Desktop links */}
        <div className="hidden md:flex items-center gap-1">
          <Link
            to="/"
            aria-label="Home"
            title="Home"
            className={`p-2 rounded transition-colors duration-150 ${pathname === '/' ? activeCls : inactiveCls}`}
          >
            <House size={15} />
          </Link>

          {NAV_LINKS.map(({ to, label }) => {
            const isActive = pathname === to

            if (label === 'Features') {
              return (
                <div
                  key={to}
                  className="relative"
                  onMouseEnter={openFeatures}
                  onMouseLeave={closeFeatures}
                >
                  <Link
                    to={to}
                    className={`inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium transition-colors duration-150 ${
                      isActive ? activeCls : inactiveCls
                    }`}
                  >
                    Features
                    <ChevronDown
                      size={13}
                      className={`transition-transform duration-200 ${featuresOpen ? 'rotate-180' : ''}`}
                    />
                  </Link>

                  <AnimatePresence>
                    {featuresOpen && (
                      <motion.div
                        initial={{ opacity: 0, y: 6 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: 6 }}
                        transition={{ duration: 0.15, ease: 'easeOut' }}
                        className="absolute top-full left-1/2 -translate-x-1/2 pt-2 z-50 w-72"
                        onMouseEnter={openFeatures}
                        onMouseLeave={closeFeatures}
                      >
                        <div className="rounded border border-theme bg-surface shadow-card overflow-hidden">
                          <div className="p-2 grid grid-cols-2 gap-0.5">
                            {NAV_FEATURES.map(({ id, name, Icon }) => (
                              <Link
                                key={id}
                                to={`/features#${id}`}
                                onClick={() => setFeaturesOpen(false)}
                                className="flex items-center gap-2.5 px-3 py-2.5 rounded text-[var(--ink-soft)] hover:text-accent hover:bg-sunk transition-colors duration-150 group/item"
                              >
                                <Icon size={13} className="flex-shrink-0 text-[var(--ink-soft)] group-hover/item:text-accent transition-colors" />
                                <span className="text-xs font-medium leading-tight">{name}</span>
                              </Link>
                            ))}
                          </div>
                          <div className="border-t border-theme px-3 py-2.5">
                            <Link
                              to="/features"
                              onClick={() => setFeaturesOpen(false)}
                              className="flex items-center justify-center gap-1.5 text-xs font-semibold text-accent"
                            >
                              View all features <ArrowRight size={11} />
                            </Link>
                          </div>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              )
            }

            if (label === 'Products') {
              return (
                <div
                  key={to}
                  className="relative"
                  onMouseEnter={openProducts}
                  onMouseLeave={closeProducts}
                >
                  <Link
                    to={to}
                    className={`inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium transition-colors duration-150 ${
                      isActive ? activeCls : inactiveCls
                    }`}
                  >
                    Products
                    <ChevronDown
                      size={13}
                      className={`transition-transform duration-200 ${productsOpen ? 'rotate-180' : ''}`}
                    />
                  </Link>

                  <AnimatePresence>
                    {productsOpen && (
                      <motion.div
                        initial={{ opacity: 0, y: 6 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: 6 }}
                        transition={{ duration: 0.15, ease: 'easeOut' }}
                        className="absolute top-full left-1/2 -translate-x-1/2 pt-2 z-50 w-72"
                        onMouseEnter={openProducts}
                        onMouseLeave={closeProducts}
                      >
                        <div className="rounded border border-theme bg-surface shadow-card overflow-hidden">
                          <div className="p-2 grid grid-cols-2 gap-0.5">
                            {NAV_PRODUCTS.map(({ id, name, Icon }) => (
                              <Link
                                key={id}
                                to="/products"
                                onClick={() => setProductsOpen(false)}
                                className="flex items-center gap-2.5 px-3 py-2.5 rounded text-[var(--ink-soft)] hover:text-accent hover:bg-sunk transition-colors duration-150 group/item"
                              >
                                <Icon size={13} className="flex-shrink-0 text-[var(--ink-soft)] group-hover/item:text-accent transition-colors" />
                                <span className="text-xs font-medium leading-tight">{name}</span>
                              </Link>
                            ))}
                          </div>
                          <div className="border-t border-theme px-3 py-2.5">
                            <Link
                              to="/products"
                              onClick={() => setProductsOpen(false)}
                              className="flex items-center justify-center gap-1.5 text-xs font-semibold text-accent"
                            >
                              View all hardware <ArrowRight size={11} />
                            </Link>
                          </div>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              )
            }

            return (
              <Link
                key={to}
                to={to}
                className={`px-4 py-2 text-sm font-medium transition-colors duration-150 ${
                  isActive ? activeCls : inactiveCls
                }`}
              >
                {label}
              </Link>
            )
          })}
        </div>

        {/* Right side */}
        <div className="hidden md:flex items-center gap-2">
          <button
            onClick={toggleTheme}
            aria-label="Toggle theme"
            className="w-9 h-9 rounded flex items-center justify-center text-[var(--ink-soft)] hover:text-accent hover:bg-sunk transition-colors duration-150"
          >
            {isDark ? <Sun size={16} /> : <Moon size={16} />}
          </button>
          <Link to="/contact">
            <Button variant="primary" size="sm">Get Started</Button>
          </Link>
          <a
            href="tel:+233596539060"
            className="hidden lg:flex items-center gap-1.5 px-2 py-1.5 rounded text-sm font-semibold text-[var(--ink-soft)] hover:text-accent hover:bg-sunk transition-colors duration-150 whitespace-nowrap"
          >
            <Phone size={15} className="text-accent flex-shrink-0" />
            (+233 59 653 9060)
          </a>
        </div>

        {/* Mobile right */}
        <div className="flex md:hidden items-center gap-1">
          <button
            onClick={toggleTheme}
            aria-label="Toggle theme"
            className="p-2 rounded text-[var(--ink-soft)] hover:text-accent hover:bg-sunk transition-colors duration-150"
          >
            {isDark ? <Sun size={18} /> : <Moon size={18} />}
          </button>
          <button
            className="p-2 rounded text-[var(--ink-soft)] hover:text-ink hover:bg-sunk transition-colors duration-150"
            onClick={() => setOpen(v => !v)}
            aria-label="Toggle menu"
          >
            {open ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>
      </nav>

      {/* Mobile menu */}
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="md:hidden navbar-surface border-t border-theme px-4 pb-4"
          >
            <div className="flex flex-col gap-1 pt-3">
              <Link
                to="/"
                className={`px-4 py-3 rounded text-sm font-medium flex items-center gap-2 ${
                  pathname === '/' ? 'text-ink bg-sunk' : 'text-[var(--ink-soft)] hover:text-ink hover:bg-sunk'
                }`}
              >
                <House size={14} /> Home
              </Link>
              {NAV_LINKS.map(({ to, label }) => (
                <Link
                  key={to}
                  to={to}
                  className={`px-4 py-3 rounded text-sm font-medium ${
                    pathname === to ? 'text-ink bg-sunk' : 'text-[var(--ink-soft)] hover:text-ink hover:bg-sunk'
                  }`}
                >
                  {label}
                </Link>
              ))}
              <div className="mt-3 pt-3 border-t border-theme">
                <a
                  href="tel:+233596539060"
                  className="flex items-center gap-2.5 px-3 py-2.5 rounded text-sm font-semibold text-ink hover:text-accent hover:bg-sunk transition-colors duration-150"
                >
                  <Phone size={16} className="text-accent flex-shrink-0" />
                  +233 59 653 9060
                </a>
                <div className="flex gap-3 mt-2">
                  <Link to="/contact" className="flex-1">
                    <Button variant="primary" size="sm" className="w-full justify-center">Get Started</Button>
                  </Link>
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  )
}
